import { useEffect, useState, type ReactElement } from "react";
import { levelFor } from "@rietz/shared";
import { uiActions } from "../actions.js";
import { SettingsPanel } from "../settings.js";
import { closeWorkbench, setMenuTab, useHud, type MenuTab } from "../store.js";
import { ACCENT_HI } from "../theme.js";
import { formatEuros, formatMmSs } from "./format.js";
import { FriendsTab, PartyInvitePrompt } from "./friends.js";
import { isPartyLeader } from "./friendsLogic.js";
import { GearTab } from "./gear.js";
import { HideoutTab } from "./hideout.js";
import { IconDeploy, IconSignOut, TAB_ICON } from "./icons.js";
import { EmptyState, Eyebrow, Modal, Note } from "./kit.js";
import { MarketTab } from "./market.js";
import { ProfileTab } from "./profile.js";
import { QuestsTab } from "./quests.js";
import { SummaryScreen } from "./summary.js";
import { TradersTab } from "./traders.js";
import { findOwnedWeapon } from "../inventory/modLogic.js";
import { WorkbenchScreen } from "../workbench/workbench.js";

/**
 * Everything that is not a raid. One component subscribed to `screen`,
 * rendering nothing for the three values that mean a raid — a DIRECT server
 * never mounts a line of this. A fullscreen DOM overlay over the VOID world;
 * `main.ts` holds `input.setLockAllowed(false)` while it is up, since only one
 * place may arbitrate the cursor.
 */

/**
 * The strip, in draw order. A table over `MenuTab`, which lives in the STORE
 * because a right-click on one tab has to open another. The index is also the
 * digit that jumps to it.
 */
const TAB_LABELS: readonly { id: MenuTab; label: string }[] = [
  { id: "gear", label: "Gear" },
  // Right behind the loadout: the level on it decides which half of the next
  // tab's shelf is greyed out.
  { id: "profile", label: "Profile" },
  { id: "traders", label: "Traders" },
  // Right behind the counters: the same stock the other way round, one sortable
  // list. Buying is the trader's own `buy`.
  { id: "market", label: "Market" },
  { id: "hideout", label: "Hideout" },
  { id: "quests", label: "Quests" },
  { id: "friends", label: "Friends" },
  { id: "settings", label: "Settings" },
];

export function MetaScreen(): ReactElement | null {
  const screen = useHud((s) => s.screen);
  const summary = useHud((s) => s.summary);

  if (screen === "summary") {
    // The `raidEnd` and the store write that follows it are one message apart;
    // rendering nothing for that instant is better than an empty panel.
    return summary === null ? null : <SummaryScreen summary={summary} />;
  }
  if (screen !== "menu" && screen !== "queue") return null;
  return <Menu queued={screen === "queue"} />;
}

// ---------------------------------------------------------------------------

function Menu(props: { queued: boolean }): ReactElement {
  const profile = useHud((s) => s.profile);
  const profileAtMs = useHud((s) => s.profileAtMs);
  const inv = useHud((s) => s.inv);
  const stash = useHud((s) => s.container);
  const friends = useHud((s) => s.friends);
  const party = useHud((s) => s.party);
  const partyInvite = useHud((s) => s.partyInvite);
  const socialError = useHud((s) => s.socialError);
  const leaderboard = useHud((s) => s.leaderboard);
  const me = useHud((s) => s.me);
  const tab = useHud((s) => s.menuTab);
  const workbench = useHud((s) => s.workbench);

  /**
   * The gun on the bench, re-resolved every render — the pane's contract, one
   * screen up. Only on Gear: switching tabs hides the bench without forgetting
   * it, and coming back shows it again.
   */
  const bench =
    tab === "gear" && workbench !== null && workbench.open && inv !== null
      ? findOwnedWeapon(inv, workbench.weaponIid, stash)
      : null;
  // Sold, or taken into a raid: the bench has nothing to be about any more.
  const benchGone = workbench?.open === true && bench === null && inv !== null && tab === "gear";
  useEffect(() => {
    if (benchGone) closeWorkbench();
  }, [benchGone]);

  // The digit hints are off until somebody proves they use them; then they
  // stay up for the session (`.mm-tab-key` reads `data-keys` off the root).
  const [keysUsed, setKeysUsed] = useState(false);

  // ONE person queues for a squad — the leader; the button says so instead of
  // earning the refusal. Identity by USERNAME (`friendsLogic.isPartyLeader`);
  // an
  // unknown identity is treated as the leader, since this gates the start
  // button.
  const canQueue = isPartyLeader(party, me);

  // 1–8 pick a tab. Bubble phase, so a focused field sees the key first; the
  // queue modal owns the keyboard while it is up.
  const queued = props.queued;
  useEffect(() => {
    if (queued) return;
    const onKey = (ev: KeyboardEvent): void => {
      if (ev.repeat || ev.ctrlKey || ev.altKey || ev.metaKey) return;
      if (!ev.code.startsWith("Digit") || isTyping(ev.target)) return;
      const picked = TAB_LABELS[Number(ev.code.slice(5)) - 1];
      if (picked === undefined) return;
      ev.preventDefault();
      setKeysUsed(true);
      setMenuTab(picked.id);
    };
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("keydown", onKey);
    };
  }, [queued]);

  return (
    <div
      className="app-backdrop metamenu"
      data-keys={keysUsed ? "true" : undefined}
      style={{
        position: "absolute",
        inset: 0,
        display: "flex",
        flexDirection: "column",
        pointerEvents: "auto",
        userSelect: "none",
      }}
    >
      <header className="mm-top">
        <div className="mm-brand">
          <span className="mm-brand-mark">EfR</span>
          <span className="mm-brand-name">
            <b>Escape from Rietz</b>
            <small>Rietz · Tirol</small>
          </span>
        </div>

        <nav className="mm-tabs" aria-label="Menu">
          {TAB_LABELS.map((t, i) => {
            const Icon = TAB_ICON[t.id];
            return (
              <button
                key={t.id}
                type="button"
                className="mm-tab"
                aria-selected={t.id === tab}
                onClick={() => {
                  setMenuTab(t.id);
                }}
              >
                <Icon size={15} />
                {t.label}
                <span className="mm-tab-key">{i + 1}</span>
              </button>
            );
          })}
        </nav>

        <div className="mm-top-right">
          <div className="mm-player">
            <span className="mm-avatar">{profile === null ? "…" : profile.name.slice(0, 1)}</span>
            <span className="mm-player-name">
              <b>{profile?.name ?? "…"}</b>
              <small>
                {profile === null ? "Level —" : `Level ${levelFor(profile.progress.xp)}`}
              </small>
            </span>
          </div>
          <span className="mm-wallet">{formatEuros(profile?.euros ?? 0)}</span>
          <button
            type="button"
            className="metabtn metabtn-icon metabtn-ghost"
            title="Sign out"
            aria-label="Sign out"
            disabled={props.queued}
            onClick={() => {
              uiActions().logout();
            }}
          >
            <IconSignOut size={16} />
          </button>
          <button
            type="button"
            className="metadeploy"
            disabled={profile === null || !canQueue}
            title={canQueue ? "Escape from Rietz" : "Your squad leader starts the raid."}
            onClick={() => {
              uiActions().deploy();
            }}
          >
            {canQueue ? "Escape from Rietz" : "Leader queues"}
            <IconDeploy size={16} />
          </button>
        </div>
      </header>

      <main className="mm-main">
        {profile === null ? (
          <EmptyState title="Loading profile…" />
        ) : (
          // Keyed by the tab, so switching REMOUNTS and the page animates in.
          <div key={tab} className="mm-page">
            {tab === "gear" ? (
              // The kit comes from the STORE (the displayed copy, drag applied),
              // not `profile.kit`. A gun on the bench takes the whole page.
              inv === null ? null : bench === null ? (
                <GearTab inv={inv} stash={stash} />
              ) : (
                <WorkbenchScreen
                  inv={inv}
                  stash={stash}
                  weapon={bench}
                  plan={workbench?.plan ?? {}}
                  euros={profile.euros}
                  level={levelFor(profile.progress.xp)}
                  quests={profile.quests}
                  loyalty={profile.loyalty}
                />
              )
            ) : tab === "profile" ? (
              // The one tab with nothing to press: the board is not part of the
              // profile —
              // the tab asks for it itself and lives with `null` until it lands.
              <ProfileTab
                name={profile.name}
                progress={profile.progress}
                leaderboard={leaderboard}
              />
            ) : tab === "traders" ? (
              // LEVEL, QUESTS and LOYALTY ride in beside the money because the
              // shelf
              // gates on all three (`offerLock`: level → tier → quest), with the
              // same
              // arithmetic the server does before it charges anybody. All three go
              // down
              // rather than one precomputed number: the tab draws five counters'
              // standing.
              inv === null ? null : (
                <TradersTab
                  euros={profile.euros}
                  level={levelFor(profile.progress.xp)}
                  quests={profile.quests}
                  loyalty={profile.loyalty}
                  inv={inv}
                  stash={stash}
                />
              )
            ) : tab === "market" ? (
              // THE SAME FIVE INPUTS AS THE COUNTER NEXT DOOR: it is the same
              // shelves as a
              // list, and the shared `offerProblem` asks exactly what the server
              // will.
              inv === null ? null : (
                <MarketTab
                  euros={profile.euros}
                  level={levelFor(profile.progress.xp)}
                  quests={profile.quests}
                  loyalty={profile.loyalty}
                  inv={inv}
                  stash={stash}
                />
              )
            ) : tab === "friends" ? (
              // The one tab that draws nothing of its own — every row is pushed
              // state.
              // It renders before the first push too, so it takes no `inv`.
              <FriendsTab state={friends} party={party} me={me} error={socialError} />
            ) : tab === "hideout" ? (
              // Pays out of the AUTHORITATIVE stash, not the displayed one: a build
              // is
              // charged server-side. `profileAtMs` turns server timestamps into
              // countdowns.
              <HideoutTab
                hideout={profile.hideout}
                euros={profile.euros}
                stash={profile.stash}
                profileAtMs={profileAtMs}
              />
            ) : tab === "settings" ? (
              // Not about this account at all — the browser's own preferences; the
              // raid's Escape overlay draws the same component.
              <SettingsPanel />
            ) : (
              // Stash and kit from the PROFILE, as the hideout tab does: a handover
              // is
              // charged server-side against instances the server has.
              <QuestsTab quests={profile.quests} stash={profile.stash} kit={profile.kit} />
            )}
          </div>
        )}
      </main>

      {props.queued ? <QueuePanel /> : null}

      {/* Last in the tree, so it sits over the queue modal as well as over the
          tabs: an invite that arrived while the player was waiting for a raid
          is exactly the one they want to be able to answer. */}
      {partyInvite === null ? null : <PartyInvitePrompt from={partyInvite} />}
    </div>
  );
}

/** A digit meant for a text field is not a tab shortcut. */
function isTyping(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  const tag = target.tagName;
  return tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT" || target.isContentEditable;
}

// ---------------------------------------------------------------------------

/**
 * The queue, as a modal over the menu. The countdown runs LOCALLY off the last
 * `startsInMs`; `null` is a statement, not a countdown — the server is waiting
 * for players, not a clock.
 */
function QueuePanel(): ReactElement {
  const queue = useHud((s) => s.queue);
  const startsInMs = queue?.startsInMs ?? null;

  /** `performance.now()` the countdown ends at, recomputed on every push. */
  const [endsAt, setEndsAt] = useState<number | null>(null);
  const [now, setNow] = useState(() => performance.now());

  useEffect(() => {
    setEndsAt(startsInMs === null ? null : performance.now() + startsInMs);
  }, [startsInMs]);

  useEffect(() => {
    if (endsAt === null) return;
    // Four times a second: the seconds never appear to stick, at 1/15th of a
    // frame's cost.
    const timer = window.setInterval(() => {
      setNow(performance.now());
    }, 250);
    return () => {
      window.clearInterval(timer);
    };
  }, [endsAt]);

  return (
    // Above everything the gear tab floats — tiles (3), panels (20), the drag
    // ghost (40), the tooltip (60). Painting above is what makes it MODAL.
    <Modal width={360} z={65} label="Matchmaking">
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: 10,
          textAlign: "center",
        }}
      >
        <Eyebrow>Matchmaking</Eyebrow>
        <span
          className="mm-num"
          style={{ fontSize: 40, fontWeight: 700, color: ACCENT_HI, lineHeight: 1 }}
        >
          {endsAt === null ? "—" : formatMmSs(endsAt - now)}
        </span>
        <Note>
          {queue === null
            ? "Joining queue…"
            : `${queue.queued} ready${endsAt === null ? " — waiting for more" : ""}`}
        </Note>
        <button
          type="button"
          className="metabtn"
          onClick={() => {
            uiActions().leaveQueue();
          }}
        >
          Cancel
        </button>
      </div>
    </Modal>
  );
}
